import clsx from 'clsx';
import { useReducedMotion } from 'motion/react';
import * as React from 'react';

import {
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_OPENAPI,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_OPENAPI_SCHEMAS,
    BLOCK_OPENAPI_WEBHOOK,
    type DocumentSection,
    getSectionsInDoc,
    type SectionIDs,
} from '@gitbook/doc-core';
import type { SectionDelimiterBlock } from '@gitbook/doc-core/src/sections/isSectionDelimiter';
import type { Document } from '@gitbook/slate';
import { Icon } from '@gitbook/spine-icons';
import { ScrollFader, useScrollFaderController } from '@gitbook/spine-react';
import { useDebouncedFlush } from '@gitbook/util-react-hooks';

import { useURLHashSectionId } from '../../../Components/ContentEditor/common/useURLHashSectionId';
import { useNavigation } from '../../../navigation';
import type { ScrollRef } from './PageSheetView';
import { usePageActiveSections } from './usePageActiveSections';

/**
 * Outline of headings in the page.
 */
export const PageOutline = React.memo(function PageOutline(props: {
    sectionIDs: SectionIDs;
    document: Document;
    scrollRef: ScrollRef;
    className?: string;
}) {
    const { sectionIDs, document, scrollRef } = props;

    const outlineRef = React.useRef<HTMLElement>(null);
    const outlineId = React.useId();

    // Make sure we're using a non-stale version of `document`
    const docRef = React.useRef(document);
    React.useEffect(() => {
        if (docRef.current !== document) {
            docRef.current = document;
        }
    });

    const [sections, setSections] = React.useState<DocumentSection[]>([]);
    React.useEffect(() => {
        const update = () => {
            setSections(
                getSectionsInDoc(docRef.current, sectionIDs).filter((section) => {
                    if (section.type === null) {
                        return false;
                    }

                    switch (section.parentType) {
                        case 'document':
                        case 'columns':
                            return DOCUMENT_SECTION_BLOCKS.includes(section.type);
                        case 'stepper':
                            return STEPPER_SECTION_BLOCKS.includes(section.type);
                        default:
                            return false;
                    }
                })
            );
        };
        if (typeof requestIdleCallback === 'function') {
            const cb = requestIdleCallback(update);
            return () => cancelIdleCallback(cb);
        }
        update();
    }, [sectionIDs]);

    const deferredSections = React.useDeferredValue(sections);

    return (
        <aside
            data-drag-selection-ignore
            ref={outlineRef}
            aria-labelledby={outlineId}
            className={clsx(
                deferredSections.length > 1 && 'page-has-outline',
                'sticky top-0 mx-auto h-screen min-h-0 w-full py-6',
                props.className
            )}
            style={{
                maxHeight: 'calc(100vh - var(--app-unavailable-height))',
            }}
        >
            <div className="mb-3 flex items-center gap-2 pl-1.5 text-ui-base" id={outlineId}>
                <Icon.Outline className="text-muted" />
                <span>On this page</span>
            </div>

            <ActivePageOutline sections={deferredSections} scrollRef={scrollRef} />
        </aside>
    );
});

/** Blocks in the document to display in the outline */
const DOCUMENT_SECTION_BLOCKS = [
    BLOCK_HEADING_1,
    BLOCK_HEADING_2,
    BLOCK_OPENAPI,
    BLOCK_OPENAPI_OPERATION,
    BLOCK_OPENAPI_SCHEMAS,
    BLOCK_OPENAPI_WEBHOOK,
];
/** Blocks in a stepper to display in the outline */
const STEPPER_SECTION_BLOCKS = [BLOCK_HEADING_1, BLOCK_HEADING_2];

const ActivePageOutline = React.memo(function ActivePageOutline(props: {
    sections: DocumentSection[];
    scrollRef: ScrollRef;
}) {
    const { sections, scrollRef } = props;
    const activeSections = usePageActiveSections(sections, scrollRef);

    return <PageOutlineContainer sections={sections} activeSections={activeSections} />;
});

export const PageOutlineContainer = React.memo(function PageOutlineMemo(props: {
    sections: DocumentSection[];
    activeSections: string[];
}) {
    const { sections, activeSections } = props;

    const containerRef = React.useRef<HTMLDivElement>(null);
    const contentRef = React.useRef<HTMLDivElement>(null);

    const [sectionElements, collect] = useCollectElements();

    useScrollToActiveElement({ activeSections, sectionElements, containerRef });

    // Scroll fade effect
    const controller = useScrollFaderController('vertical', containerRef);

    const { indicatorTransform, trackLeft } = React.useMemo(
        () =>
            getTrackIndicatorStyle({
                activeSections,
                sections,
                sectionElements,
                contentRef,
            }),
        [activeSections, sections, sectionElements]
    );

    return (
        <ScrollFader
            className={clsx(
                'flex max-h-[65vh] min-h-0',
                // Push the scroll fade effect to the right so it only covers the outline items, not the track.
                'before:top-0 before:right-0 before:bottom-0 before:left-5 after:top-0 after:right-0 after:bottom-0 after:left-5',
                // Adjust the fade length so the first active item isn't faded out.
                'before:to-5% after:to-8%'
            )}
            controller={controller}
        >
            <div
                ref={containerRef}
                className="scrollbar relative w-full overflow-y-auto overflow-x-hidden"
            >
                <div ref={contentRef} className="relative my-2 flex flex-col">
                    {/* Track */}
                    <div
                        data-outline-track
                        className="absolute h-full w-px bg-[--sp-color-border-base]"
                        style={{ left: trackLeft }}
                    />
                    {/* Animated active section(s) indicator */}
                    <div
                        data-outline-indicator
                        className="absolute h-full w-px origin-top bg-action-primary motion-safe:transition-transform"
                        style={{ left: trackLeft, transform: indicatorTransform }}
                    />
                    <div className="flex flex-col gap-3.5">
                        {sections.map((section) => (
                            <OutlineSectionItem
                                key={section.key}
                                collect={collect}
                                section={section}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </ScrollFader>
    );
});

/**
 * Entry for a section in the outline.
 */
const OutlineSectionItem = React.memo(
    function OutlineSectionItem(props: {
        section: DocumentSection;
        collect: (key: string) => (el: HTMLElement | null) => void;
    }) {
        const { section, collect } = props;
        const { navigateToHash } = useNavigation();
        const hashSectionId = useURLHashSectionId();
        const ref = React.useMemo(() => collect(section.key), [section.key, collect]);

        if (section.anchor) {
            return (
                <a
                    ref={ref}
                    className="hyphens-auto break-words pr-6 pl-[1.75rem] text-left text-muted text-ui-base leading-[1.125rem] hover:text-primary"
                    onClick={(event) => {
                        navigateToHash(section.anchor, { scroll: true });

                        // Needs to be at the end to let scrolling happen.
                        event.preventDefault();
                    }}
                    href={`#${section.anchor}`}
                    aria-current={section.anchor === hashSectionId ? 'location' : undefined}
                >
                    {(() => {
                        switch (section.node?.type) {
                            case BLOCK_HEADING_2:
                                return <div className="pl-3">{section.title}</div>;
                            case BLOCK_OPENAPI_OPERATION:
                                return <OutlineSectionItemOpenAPI node={section.node} />;
                            case BLOCK_OPENAPI: {
                                if (section.node.data.has('ref')) {
                                    return <OutlineSectionItemOpenAPI node={section.node} />;
                                }
                                return section.title;
                            }
                            default:
                                return section.title;
                        }
                    })()}
                </a>
            );
        }
    },
    (prevProps, nextProps) =>
        Boolean(
            prevProps.section.key === nextProps.section.key &&
                prevProps.section.title === nextProps.section.title &&
                prevProps.section.node?.type === nextProps.section.node?.type &&
                prevProps.section.node?.data.equals(nextProps.section.node?.data) &&
                prevProps.collect === nextProps.collect &&
                prevProps.section.anchor === nextProps.section.anchor
        )
);

const OutlineSectionItemOpenAPI = React.memo(function OutlineSectionItemOpenAPI(props: {
    node: SectionDelimiterBlock;
}) {
    const { node } = props;
    const method = node.data.get('method');
    const path = node.data.get('path');
    return (
        <span className="leading-5">
            <span className={`openapi-method openapi-method-xsmall openapi-method-${method}`}>
                {method.toUpperCase()}
            </span>
            <span>{path}</span>
        </span>
    );
});

function useScrollToActiveElement(props: {
    activeSections: string[];
    sectionElements: Record<string, HTMLElement | undefined | null>;
    containerRef: React.RefObject<HTMLDivElement>;
}) {
    const { activeSections, sectionElements, containerRef } = props;
    const isReducedMotion = useReducedMotion();
    // Debounce the scroll to avoid a jerky scroll experience.
    const debounceScroll = useDebouncedFlush(isReducedMotion ? 0 : 125);
    React.useEffect(() => {
        const container = containerRef.current;
        if (!container) {
            return;
        }

        // Only sync scroll when the outline is scrollable.
        if (container.scrollHeight <= container.clientHeight) {
            return;
        }

        const activeSection = activeSections[0];
        if (!activeSection) {
            return;
        }

        const element = sectionElements[activeSection];
        if (!element) {
            return;
        }

        // Align the outline scroll position with the first visible section.
        // This aligns outline scroll position with the page scroll position.
        const top = Math.max(element.offsetTop - 20, 0);
        if (container.scrollTop !== top) {
            debounceScroll(() =>
                container.scrollTo({
                    top,
                    behavior: isReducedMotion ? 'instant' : 'smooth',
                })
            );
        }
    }, [activeSections, debounceScroll, isReducedMotion, sectionElements, containerRef]);
}

function useCollectElements() {
    const [elements, setElements] = React.useState<Record<string, HTMLElement | null | undefined>>(
        {}
    );
    const collect = React.useCallback(
        (key: string) => (el: HTMLElement | null) => {
            setElements((prev) => ({ ...prev, [key]: el }));
        },
        []
    );
    return [elements, collect] as const;
}

/**
 * Get the transform of the active section indicator.
 */
function getTrackIndicatorStyle(props: {
    sections: DocumentSection[];
    activeSections: string[];
    sectionElements: Record<string, HTMLElement | undefined | null>;
    contentRef: React.RefObject<HTMLDivElement | null>;
}) {
    const { sections, activeSections, sectionElements, contentRef } = props;

    const contentElement = contentRef.current;
    if (!contentElement) {
        return {
            indicatorTransform: `translateY(0px) scaleY(0)`,
            trackLeft: TRACK_LEFT,
        };
    }
    const contentHeight = contentElement.clientHeight;
    if (contentHeight === 0) {
        return {
            indicatorTransform: `translateY(0px) scaleY(0)`,
            trackLeft: TRACK_LEFT,
        };
    }

    const activeKeys = activeSections.length > 0 ? activeSections : [sections[0].key];
    const lastSection = sections.at(-1);

    const isFirstSectionActive = activeKeys[0] === sections[0].key;
    const isLastSectionActive = Boolean(lastSection && activeKeys.includes(lastSection.key));
    const firstActiveSection = activeKeys[0];
    const firstActiveSectionElement = sectionElements[firstActiveSection] ?? null;

    const rawTop = isFirstSectionActive
        ? 0
        : firstActiveSectionElement
          ? firstActiveSectionElement.offsetTop
          : 0;

    const lastActiveSection = activeKeys[activeKeys.length - 1];
    const lastActiveSectionElement = sectionElements[lastActiveSection] ?? null;

    const rawBottom = isLastSectionActive
        ? contentHeight
        : lastActiveSectionElement
          ? lastActiveSectionElement.offsetTop + lastActiveSectionElement.clientHeight
          : rawTop;

    const top = Math.min(Math.max(rawTop, 0), contentHeight);
    const bottom = Math.min(Math.max(rawBottom, top), contentHeight);
    const availableHeight = Math.max(contentHeight - top, 0);
    const baseHeight = Math.max(bottom - top, 0);
    const height = Math.min(Math.max(baseHeight, MIN_INDICATOR_HEIGHT), availableHeight);
    const scaleY = height <= 0 ? 0 : height / contentHeight;

    return {
        indicatorTransform: `translateY(${top}px) scaleY(${scaleY})`,
        trackLeft: TRACK_LEFT,
    };
}

const TRACK_LEFT = 6;
const MIN_INDICATOR_HEIGHT = 20;
